<template>
  <div class="summary-cr-presence">
    <div class="text-subtitle-2 font-weight-medium mb-2">Наличие в КР</div>
    <v-switch
      v-if="editMode"
      v-model="local.hasInCr"
      label="Есть ли ЛП в КР?"
      color="primary"
      hide-details
      class="mt-0 mb-3"
      @change="onHasInCrChange"
    />
    <div v-else class="mb-2">
      Есть ли ЛП в КР:
      <span class="font-weight-medium">{{ local.hasInCr ? 'Да' : 'Нет' }}</span>
    </div>

    <template v-if="local.hasInCr">
      <v-data-table
        v-if="tableItems.length"
        :items="tableItems"
        :headers="headers"
        show-expand
        disable-pagination
        hide-default-footer
        disable-sort
        item-key="idx"
        :expanded.sync="expanded"
        class="mb-2 summary-cr-presence-table"
      >
        <template #[`item.idx`]="{ item }">
          <span class="text--disabled">{{ item.idx }}.</span>
        </template>
        <template #[`item.name`]="{ value }">
          <span v-if="value">{{ value }}</span>
          <span v-else class="warning--text">( пусто )</span>
        </template>
        <template #[`item.crNumber`]="{ value }">
          <span v-if="value">{{ value }}</span>
          <span v-else class="warning--text">—</span>
        </template>
        <template #[`item.year`]="{ value }">
          <span v-if="value">{{ value }}</span>
          <span v-else class="warning--text">—</span>
        </template>
        <template #[`item.reviewBy`]="{ value }">
          <span v-if="value">{{ value }}</span>
          <span v-else class="warning--text">—</span>
        </template>
        <template v-slot:expanded-item="{ headers: h, item }">
          <td :colspan="h.length" class="td-expanded pa-3">
            <CrArgumentsEditor
              :value="item.arguments"
              :readonly="readonly"
              @change="args => onArgumentsChange(item.idx, args)"
              @blur="$emit('blur')"
            />
          </td>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-btn
            v-if="editMode"
            icon
            small
            title="Редактировать КР"
            @click.stop="openEditDialog(item.idx - 1)"
          >
            <v-icon small>mdi-pencil-outline</v-icon>
          </v-btn>
        </template>
        <template v-slot:[`item.data-table-expand`]="{ expand, isExpanded }">
          <v-btn
            icon
            @click="expand(!isExpanded)"
            class="v-data-table__expand-icon"
            :class="{ 'v-data-table__expand-icon--active': isExpanded }"
          >
            <v-icon>mdi-pencil-circle-outline</v-icon>
          </v-btn>
        </template>
        <template v-if="editMode" #footer>
          <div class="d-flex pt-2">
            <v-btn color="primary" text small @click="openAddDialog">
              <v-icon left small>mdi-plus</v-icon>
              Добавить КР
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <v-btn
        v-else-if="editMode"
        color="primary"
        text
        small
        class="mb-3"
        @click="openAddDialog"
      >
        <v-icon left small>mdi-plus</v-icon>
        Добавить КР
      </v-btn>

      <v-alert
        v-if="!editMode && presentationPreview"
        type="info"
        outlined
        dense
        class="text-pre-wrap mb-0"
      >
        {{ presentationPreview }}
      </v-alert>
    </template>
    <div v-else-if="!editMode" class="text--secondary">Нет</div>

    <CrItemDialog
      :dialog-show="dialogShow"
      :item="editingItem"
      @save="onDialogSave"
      @remove="onDialogRemove"
      @close="closeDialog"
    />
  </div>
</template>

<script>
import CrItemDialog from './CrItemDialog.vue'
import CrArgumentsEditor from './CrArgumentsEditor.vue'
import {
  DEFAULT_CR_PRESENCE,
  DEFAULT_SUMMARY_CR_ITEM,
  formatCrPresencePresentation,
  normalizeCrPresence,
} from './crPresenceConst'

export default {
  name: 'SummaryCrPresence',
  components: { CrItemDialog, CrArgumentsEditor },
  props: {
    value: { type: Object, default: null },
    legacyCrNames: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
    editMode: { type: Boolean, default: false },
  },
  data: () => ({
    local: DEFAULT_CR_PRESENCE(),
    expanded: [],
    dialogShow: false,
    editingIndex: null,
    editingItem: null,
    headers: [
      { text: '№', value: 'idx', width: '48px' },
      { text: 'Название', value: 'name' },
      { text: '№ КР', value: 'crNumber', width: '90px' },
      { text: 'Год', value: 'year', width: '72px' },
      { text: 'Пересмотр', value: 'reviewBy', width: '100px' },
      { text: '', value: 'actions', width: '88px', sortable: false },
      { text: '', value: 'data-table-expand', width: '48px' },
    ],
  }),
  computed: {
    tableItems() {
      return this.local.items.map((item, i) => ({
        ...item,
        idx: i + 1,
      }))
    },
    presentationPreview() {
      return formatCrPresencePresentation(this.local, this.legacyCrNames)
    },
  },
  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(v) {
        this.local = normalizeCrPresence(v, this.legacyCrNames)
      },
    },
    tableItems: {
      immediate: true,
      handler(items) {
        this.expandAll(items)
      },
    },
  },
  mounted() {
    this.expandAll()
  },
  methods: {
    expandAll(items = this.tableItems) {
      this.expanded = items?.length ? [...items] : []
    },
    expandItem(item = null) {
      this.expanded = item ? [item] : []
    },
    emitChange(force = false) {
      this.$emit('change', { ...this.local }, force)
    },
    onHasInCrChange() {
      if (!this.local.hasInCr) {
        this.expandItem()
      } else {
        this.$nextTick(() => this.expandAll())
      }
      this.emitChange(true)
    },
    openAddDialog() {
      this.editingIndex = null
      this.editingItem = null
      this.dialogShow = true
    },
    openEditDialog(index) {
      this.editingIndex = index
      this.editingItem = { ...this.local.items[index] }
      this.dialogShow = true
    },
    closeDialog() {
      this.dialogShow = false
      this.editingIndex = null
      this.editingItem = null
    },
    onDialogSave(item) {
      const row = {
        ...DEFAULT_SUMMARY_CR_ITEM(),
        ...item,
        arguments:
          item.arguments?.length > 0
            ? item.arguments
            : DEFAULT_SUMMARY_CR_ITEM().arguments,
      }
      if (this.editingIndex == null) {
        this.local.items.push(row)
      } else {
        this.$set(this.local.items, this.editingIndex, row)
      }
      this.emitChange(true)
      this.$nextTick(() => this.expandAll())
    },
    onDialogRemove() {
      if (this.editingIndex == null) return
      this.local.items.splice(this.editingIndex, 1)
      this.emitChange(true)
      this.$nextTick(() => this.expandAll())
    },
    onArgumentsChange(idx, args) {
      const index = idx - 1
      if (index < 0 || !this.local.items[index]) return
      this.$set(this.local.items[index], 'arguments', args)
      this.emitChange()
    },
  },
}
</script>

<style scoped>
.summary-cr-presence {
  max-width: 100%;
}
.summary-cr-presence-table >>> .v-data-table__wrapper tbody tr.v-data-table__expanded__content {
  box-shadow: none;
}
.td-expanded {
  background: linear-gradient(to bottom, rgb(255, 255, 255) 10%, #dfdfdf 100%);
  border-radius: 0 0 15px 15px;
}
.v-data-table__expand-icon--active {
  background-color: lightgreen;
}
</style>
